import {
  AttachmentConfig,
  DEFAULT_ATTACHMENT_CONFIG,
  sanitizeAttachmentConfig,
  toAcceptedExtensions,
} from "helpers/attachmentConfig";
import { SignerAttachment } from "models/interfaces";

export interface SignerAttachmentItem {
  id: string;
  file?: File;
  name: string;
  size: number;
  addedAt: string;
}

let sessionPolicy: AttachmentConfig = { ...DEFAULT_ATTACHMENT_CONFIG };

export const setAttachmentPolicy = (raw: unknown) => {
  if (raw) {
    sessionPolicy = sanitizeAttachmentConfig(raw);
  }
};

export const getAttachmentPolicy = (): {
  acceptedFiles: string[];
  maxFileSizeMb: number;
  maxFiles: number;
  allowedTypes: string[];
} => ({
  acceptedFiles: toAcceptedExtensions(sessionPolicy.allowedTypes),
  maxFileSizeMb: sessionPolicy.maxFileSizeMb,
  maxFiles: sessionPolicy.maxFilesPerSession,
  allowedTypes: sessionPolicy.allowedTypes,
});

export const formatAllowedTypesSentence = (allowedTypes: string[]): string => {
  const labels = allowedTypes.map((type) => type.toUpperCase());
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
};

export const formatAttachmentSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatAddedAt = (date: Date = new Date()): string => {
  const period = date.getHours() >= 12 ? "PM" : "AM";
  const hours = date.getHours() % 12 || 12;
  return `${hours}:${String(date.getMinutes()).padStart(2, "0")} ${period}`;
};

/**
 * Only what the browser itself can paint counts as `image` — `.tiff` and
 * `.heic` are allowed uploads but no mainstream browser renders them, so they
 * fall through to `other` and the preview says so rather than showing a broken
 * image. Everything non-visual (DOC, XLS, TXT) is `other` too.
 */
export const attachmentFileKind = (
  name: string,
): "pdf" | "image" | "other" => {
  const extension = name.slice(name.lastIndexOf(".")).toLowerCase();
  if (extension === ".pdf") return "pdf";
  if ([".jpg", ".jpeg", ".png", ".gif", ".bmp"].includes(extension))
    return "image";
  return "other";
};

/** Turns a saved server record into a UI row. */
export const toAttachmentItem = (
  attachment: SignerAttachment,
  file?: File,
): SignerAttachmentItem => ({
  id: attachment.id,
  file,
  name: attachment.fileName,
  size: attachment.sizeBytes,
  addedAt: attachment.uploadedOn
    ? formatAddedAt(new Date(attachment.uploadedOn))
    : formatAddedAt(),
});

export const encodeAttachmentValue = (items: SignerAttachmentItem[]): string =>
  items.length === 0 ? "" : JSON.stringify(items.map((item) => item.id));

export const decodeAttachmentValue = (value?: string): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map((entry) => String(entry)) : [];
  } catch {
    return [];
  }
};
